import * as Sentry from '@sentry/node';
import React from 'react';
import App from 'next/app';
import { defaultPrimaryColorScheme } from '@nubank/nuds-web/components/NuDSProvider/NuDSProvider';
import nuDSTheme from '@nubank/nuds-web/styles/theme';
import Router from 'next/router';
import { isType } from '@nubank/nuds-web/utils/isType/isType';

import WebsiteProvider from '@nubank/www-latam-commons/components/WebsiteProvider/WebsiteProvider';
import ImgGlobalStyle from '@nubank/www-latam-commons/styles/ImgGlobalStyles';
import addWebpSupportClass from '@nubank/www-latam-commons/utils/browserFeatures/webp/addWebpSupportClass';
import { persistMarketingURL } from '@nubank/www-latam-commons/utils/marketingUtils';
import { persistMGMParameters } from '@nubank/www-latam-commons/utils/mgmUtils';
import { queryStringToParameters } from '@nubank/www-latam-commons/utils/urlUtils';
import { compose } from '@nubank/www-latam-commons/utils/functionalUtils';
import printJoinUsMessage from '@nubank/www-latam-commons/utils/asciiart/printJoinUsMessage';
import { IGNORE_ERRORS, IGNORE_URLS } from '@nubank/www-latam-commons/utils/sentry';
import setReturningApplicantOnDataLayer from 'domains/prospect/setReturningApplicantOnDataLayer';
import theme from 'styles/theme';
import RouterLink from 'components/RouterLink/RouterLink';
import { AnnouncementContextProvider } from 'components/AnnouncementBar/AnnouncementBarProvider';
import announcement, { canBeDisplayed } from 'components/AnnouncementBar/announcements/survey';
import { DEFAULT_LOCALE } from 'utils/i18nUtils';
import sendPersonaToGTM from 'tracking/navegg/sendPersonaToGTM';
import { triggerOptimize } from 'tracking/optimize/triggerOptimize';
import * as experimentsUtils from 'utils/experiments';

import { cmsService } from '../services/cms/cmsService';

import routes from './routes.json';

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  enabled: process.env.NODE_ENV === 'production',
  ignoreErrors: IGNORE_ERRORS,
  ignoreUrls: IGNORE_URLS,
});

// TODO: Remove it afeter we finish the first version of NuDSTheme
export const themeMergedWithNuDS = {
  ...nuDSTheme,
  ...theme,
};

export default class CustomApp extends App {
  constructor(props, ctx) {
    super(props, ctx);
    this.state = {
      locale: props.locale,
      messages: props.messages,
    };

    printJoinUsMessage('https://boards.greenhouse.io/nubank?gh_src=b58135231');

    // workaround to remove trailing slash at the end of href in static export
    // https://github.com/zeit/next.js/pull/3283
    if (typeof __NEXT_DATA__ !== 'undefined') {
      __NEXT_DATA__.nextExport = false;
    }
  }

  static async getInitialProps({ Component, router, ctx }) {
    try {
      const pageProps = Component.getInitialProps
        ? await Component.getInitialProps(ctx)
        : {};

      const { query: { locale = DEFAULT_LOCALE }, asPath } = router;
      let messages = {};
      let cms = {};

      if (typeof window === 'undefined') {
        cms = await cmsService({
          locale,
          preview: asPath.includes('preview=true'),
          cache: !asPath.includes('cache=false'),
        });
      }

      if (typeof window === 'undefined') {
        messages = require(`../lang/${locale}.json`);
      }

      return {
        pageProps,
        locale,
        messages: { ...cms, ...messages },
        pathname: router.pathname,
      };
    } catch (err) {
      throw new Error(err.message);
    }
  }

  async componentDidMount() {
    persistMarketingURL(document.location);

    compose(
      persistMGMParameters(),
      queryStringToParameters,
    )(document.location.search);

    setReturningApplicantOnDataLayer();

    await sendPersonaToGTM();

    addWebpSupportClass(document.querySelector('body'));

    triggerOptimize();
    Router.events.on('routeChangeComplete', triggerOptimize);
  }

  render() {
    const {
      Component, pageProps, locale, messages, pathname,
    } = this.props;

    // TODO: Have a better way to extract this info in future
    const pageColorSchemeVariant = Object.values(routes).reduce((pageColorScheme, route) => {
      const routePathname = route.routes['pt-BR'];
      const hasRoutePageColorScheme = Boolean(route.pageColorScheme);
      const currentPagePathname = this.props.router.pathname;
      if (hasRoutePageColorScheme && routePathname === currentPagePathname) {
        return route.pageColorScheme;
      }
      return pageColorScheme;
    }, defaultPrimaryColorScheme);

    const hasAnnouncementRestrictions = locale !== DEFAULT_LOCALE || [
      '/pedir/nu',
      '/pedir/conta',
      '/indicacao/nu',
      '/indicacao/nu/_AQmDP-5RfGrRTxjKLZpUA/1',
      '/transparente',
      '/carreiras',
      '/contato',
      '/contrato',
      '/contrato/cartao',
      '/contrato/emprestimo-pessoal',
      '/contrato/conta',
      '/contrato/politica-de-compliance',
      '/contrato/politica-privacidade',
      '/contrato/politica-seguranca',
      '/contrato/regulamento-rewards',
      '/contrato/sorteio',
      '/auxilio-emergencial-fgts',
    ].includes(pathname);

    const availableExperiments = Object.values(experimentsUtils)
      .filter(value => isType('object', value))
      .filter(object => Object.keys(object).includes('id') && Object.keys(object).includes('variants'));

    const customPolyfills = [
      'Intl',
      'Intl.RelativeTimeFormat',
      'Intl.PluralRules.~locale.pt',
      'Intl.DateTimeFormat.~locale.pt',
      'Intl.NumberFormat.~locale.pt',
      'Intl.RelativeTimeFormat.~locale.pt',
      'Intl.RelativeTimeFormat.~locale.en',
    ];

    return (
      <>
        <ImgGlobalStyle />
        <WebsiteProvider
          locale={this.state?.locale || locale}
          messages={this.state?.messages || messages}
          theme={themeMergedWithNuDS}
          colorScheme={pageColorSchemeVariant}
          routerLinkComponent={RouterLink}
          availableExperiments={availableExperiments}
          customPolyfillFeatures={customPolyfills}
        >
          <AnnouncementContextProvider
            {...announcement}
            isAllowed={!hasAnnouncementRestrictions && canBeDisplayed}
          >
            <Component {...pageProps} />
          </AnnouncementContextProvider>
        </WebsiteProvider>
      </>
    );
  }
}
